import { useRouter } from "next/router"
import { useSelector } from "react-redux"

import { IProject, UserRole } from "@api/schema"
import { Routes } from "@basics/routes"
import { humanTime } from "@basics/util-importless"
import AuthElement from "@components/common/AuthElement"
import DropdownComponent from "@components/common/DropdownComponent"
import FAQFlyoutTriggerIcon from "@components/common/faq/FAQFlyoutTriggerIcon"
import Icon from "@components/common/Icon"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import { SINN_BASE_URL } from "@customer-config"
import { EntityType } from "@redux/common/reduxTypes"
import { selectCollection } from "@redux/common/scopedObject/selectors"
import { AppState } from "@redux/reducer"
import { selectIsAuthenticated } from "@redux/reducer/auth"
import { useTooltip } from "@services/hooks/useTooltip"
import { NamespaceShortcut, useDynamicTranslation } from "@services/i18n/i18n"
import { getStringFromQuery } from "@services/routesHelper"
import { MODULE_LOGOUT_TIMER_AVAILABLE, SINGLE_PROVIDER_SINGLE_PROGRAM, SINN_PROTOTYPE_CLIENT_CONFIG_USED } from "config"

import NavIcon from "./NavIcon"
import NavigationDropDownItem from "./NavigationDropDownItem"

const usedNamespaces: NamespaceShortcut[] = ["base-layout", "external-provider"]

interface IProps {
  // @todo diese ganzen Properties nicht aus den Elternkomponenten holen sondern
  // aus mapStateToProps damit die Elternkomponenten nicht neu gerendert werden weil sich
  // props ändern die sie nicht brauchen sondern nur durchreichen: https://i.imgur.com/xpwcEH3.png
  authTTL: number
  doLogout: (redirect: Routes) => void
  isFrontPage?: boolean
}

/**
 * provides the navigation icons on every page of the application
 */
const HeaderIconNavigation: React.FC<IProps> = (props: IProps) => {
  const t = useDynamicTranslation()

  const { tooltipTargetRef, ToolTip } = useTooltip(t("base-layout", "timeTillLogout"))
  const isLoggedIn = useSelector((state: AppState) => selectIsAuthenticated(state))

  // only for SINN: retrieve project; if given then show the "Help & Feedback" button
  const router = useRouter()
  const projectSlug = getStringFromQuery(router, "slug")
  const project = useSelector((state: AppState) => selectCollection<IProject>(state, EntityType.Project)
    .find(p => p.slug === projectSlug)
  )

  return <nav role="navigation" className="icon-navigation">
    <AuthElement requiredUserRole={UserRole.PlatformManager}>
      <NavIcon href={Routes.PlatformManagerDashboard} icon="monitor" title={t("goto", "platform.dashboard")} />
    </AuthElement>

    {!SINN_PROTOTYPE_CLIENT_CONFIG_USED && <NavIcon
      href={Routes.MarketOfPrograms}
      icon="market"
      title={t("base-layout", "marketplace")}
    />}

    {!SINN_PROTOTYPE_CLIENT_CONFIG_USED && SINGLE_PROVIDER_SINGLE_PROGRAM && <NavIcon
      href={Routes.SingleMultiCurrentProgram}
      icon="info-bubble"
      title={t("base-layout", "programOverview")}
    />}

    {SINN_PROTOTYPE_CLIENT_CONFIG_USED && project?.externalIdentifier && <NavIcon href={`${SINN_BASE_URL}/portal/innovation/${project?.externalIdentifier}/settings#support`} icon="comment-ask" title={t("base-layout", "feedbackAndHelp")} />}

    <DropdownComponent
      autoWrap={false}
      tooltipPlacement="top"
      button={
        // classname "actionicon" for harmonizing spacing to other icons and color on hover
        <Icon className="actionicon" name="user" />
      }
      light={props.isFrontPage}
      title={t("base-layout", "user")}
      className="actionicon"
    >
      {isLoggedIn && SINN_PROTOTYPE_CLIENT_CONFIG_USED &&
        <NavigationDropDownItem
          label={t("external-provider", "sinn.goto.sinnDashboard")}
          href={`${SINN_BASE_URL}/portal/dashboard`}
          target="_blank"
        />
      }
      {isLoggedIn &&
        <NavigationDropDownItem
          label={t("base-layout", "userDashboard")}
          href={Routes.UserDashboard}
        />
      }

      {isLoggedIn &&
        <NavigationDropDownItem
          label={t("base-layout", "myProjects")}
          href={Routes.MyProjects}
        />
      }
      {isLoggedIn &&
        <NavigationDropDownItem
          label={t("base-layout", "logout")}
          onClick={() => props.doLogout(Routes.MarketOfPrograms)}
        />
      }
      {!isLoggedIn &&
        <NavigationDropDownItem
          href={Routes.Login}
          label={t("base-layout", "login")}
        />
      }
    </DropdownComponent>

    {MODULE_LOGOUT_TIMER_AVAILABLE && props.authTTL > 0 && isLoggedIn && <>
      <div ref={tooltipTargetRef}>{humanTime(props.authTTL)}</div>
      {ToolTip}
    </>
    }
    {!SINN_PROTOTYPE_CLIENT_CONFIG_USED &&
      <FAQFlyoutTriggerIcon className="actionicon faq-icon" />
    }
  </nav >
}

export default withDynamicNamespaces<IProps>(HeaderIconNavigation, usedNamespaces)